import React, { useState } from 'react';
import { Table, Button, Tag, Space, Tooltip } from 'antd';
import { CheckCircleOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { DndContext, PointerSensor, useSensor } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const getMaterialStatusTag = (status) => {
  if (!status || status === "-") return "-";
  
  const statusMap = {
    'in stock': { color: 'green', text: 'In Stock' },
    'no stock': { color: 'orange', text: 'No Stock' }
  };
  
  const { color, text } = statusMap[status] || { color: 'default', text: status };
  return <Tag color={color}>{text}</Tag>;
};


const SortableRow = ({ children, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      {children}
    </tr>
  );
};


const RegularRow = ({ children, ...props }) => {
  return <tr {...props}>{children}</tr>;
};

const ActiveTasksTable = ({ tasks, onComplete, onDelete, onEdit, isSorting, onReorder, materials, loading = false }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  
  const sensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 1,
    },
  });

  
  const validTasks = Array.isArray(tasks) ? tasks : [];

  
  const prioritizedTasks = validTasks.filter(task => task.jobStatus === "red");
  const normalTasks = validTasks.filter(task => task.jobStatus !== "red");

  
  const getMaterialName = (materialId) => {
    if (!materialId || materialId === "-") return "-";
    const material = materials.find(m => m.id === materialId);
    return material ? material.name : materialId; 
  };

  
  const getStatusTag = (status) => {
    if (!status || status === "-") return "-";
    
    const statusMap = {
      red: { color: 'red', text: 'Started' },
      yellow: { color: 'gold', text: 'Waiting' },
      green: { color: 'green', text: 'Ready' },
      gray: { color: 'default', text: 'Missing Material' }
    };
    
    const { color, text } = statusMap[status] || { color: 'default', text: status };
    return <Tag color={color}>{text}</Tag>;
  };

  
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        return "Invalid date";
      }
      return date.toLocaleString('en-US');
    } catch (error) {
      console.error('Error formatting date:', error);
      return "Error";
    }
  };

  
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    
    if (!over || !active || !active.id || !over.id) {
      return;
    }
    
    if (active.id !== over.id) {
      
      const activeId = String(active.id);
      const overId = String(over.id);
      
      
      const isActivePrioritized = prioritizedTasks.some(task => String(task.id) === activeId);
      if (isActivePrioritized) {
        return; 
      }
      
      const activeIndex = normalTasks.findIndex(task => String(task.id) === activeId);
      const overIndex = normalTasks.findIndex(task => String(task.id) === overId);
      
      
      if (activeIndex === -1 || overIndex === -1) {
        return; 
      }
      
      
      const newNormalTasks = [...normalTasks];
      const [movedItem] = newNormalTasks.splice(activeIndex, 1);
      newNormalTasks.splice(overIndex, 0, movedItem);
      
      
      const newTasks = [...prioritizedTasks, ...newNormalTasks];
      onReorder(newTasks);
    }
  };

  
  const columns = [
    {
      title: 'Job Number',
      dataIndex: 'jobNumber',
      key: 'jobNumber',
      render: text => text || "-", 
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      render: text => text || "-", 
    },
    {
      title: 'Material',
      dataIndex: 'material',
      key: 'material',
      render: materialId => getMaterialName(materialId)
    },
    {
      title: 'Material Quantity',
      dataIndex: 'materialQuantity',
      key: 'materialQuantity',
      render: text => text || "-", 
    },
    {
      title: 'Material Status',
      dataIndex: 'materialStatus',
      key: 'materialStatus',
      render: (status, record) => {
        
        if (record.jobStatus === "red") {
          return "-";
        }
        return getMaterialStatusTag(status);
      }
    },
    {
      title: 'Machine',
      dataIndex: 'machine',
      key: 'machine',
      render: text => text || "-", 
    },
    {
      title: 'Job Status',
      dataIndex: 'jobStatus',
      key: 'jobStatus',
      render: status => getStatusTag(status),
    },
    {
      title: 'Time Stamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: timestamp => formatDate(timestamp),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => onEdit(record)}
              disabled={isSorting || loading}
            />
          </Tooltip>
          {/* 只有 started 状态(red)的任务才能完成 */}
          {record.jobStatus === "red" && (
            <Tooltip title="Complete">
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                size="small"
                onClick={() => onComplete(record.id)}
                disabled={isSorting || loading}
                loading={loading}
              />
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => onDelete(record.id)}
              disabled={isSorting || loading}
              loading={loading}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  
  const components = isSorting
    ? {
        body: {
          row: (props) => {
            
            if (!props['data-row-key']) {
              return <tr {...props}>{props.children}</tr>;
            }
            
            
            const isPrioritized = prioritizedTasks.some(
              task => String(task.id) === props['data-row-key']
            );
            
            
            return isPrioritized ? 
              <RegularRow {...props} /> : 
              <SortableRow {...props} />;
          },
        },
      }
    : {};

  
  const combinedTasks = [...prioritizedTasks, ...normalTasks];

  return (
    <div>
      {/* Render prioritized tasks section if any exist */}
      {prioritizedTasks.length > 0 && (
        <div style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#fff1f0', borderRadius: '4px' }}>
          <strong>To ensure efficiency, tasks that have already started cannot be rescheduled - Always shown at the top</strong>
        </div>
      )}
      
      <DndContext 
        sensors={[sensor]} 
        onDragEnd={handleDragEnd} 
        disabled={!isSorting}
      >
        <SortableContext
          items={normalTasks.map(task => String(task.id))}
          strategy={verticalListSortingStrategy}
          disabled={!isSorting}
        >
          <Table
            dataSource={combinedTasks}
            columns={columns}
            rowKey={record => String(record.id)}
            pagination={{
              pageSize,
              current: currentPage,
              onChange: page => setCurrentPage(page),
              showSizeChanger: false,
            }}
            components={components}
            style={{ opacity: isSorting ? 0.9 : 1 }}
            loading={loading && !isSorting}
          />
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default ActiveTasksTable;